import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { api } from '../services/api';

const monthKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

const shiftMonth = (key, delta) => {
  const [year, month] = key.split('-').map(Number);
  return monthKey(new Date(year, month - 1 + delta, 1));
};

// A postpaid workspace is invoiced monthly: its user sees each document charged this month, in characters only -
// nothing to buy, no free characters.
export default function PostpaidMonth({ usage }) {
  const { t, i18n } = useTranslation('settings');
  const thisMonth = monthKey(new Date());
  const [month, setMonth] = useState(thisMonth);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const n = (value) => value.toLocaleString(i18n.language);
  const monthTitle = (key) => {
    const [year, m] = key.split('-').map(Number);
    return new Date(year, m - 1, 1).toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' });
  };

  const load = useCallback(async (key) => {
    setData(null);
    setError(null);
    try {
      setData(await api.getBillingMonth(key));
    } catch (e) {
      setError(t('usage.postpaid.loadError'));
    }
  }, [t]);

  useEffect(() => {
    load(month);
  }, [load, month]);

  return (
    <>
      <section className="usage-balance">
        <div className="usage-stat">
          <span className="num" data-testid="postpaid-month-chars">{data ? n(data.totals.billed_chars) : '…'}</span>
          <span className="lbl">{t('usage.postpaid.monthChars', { month: monthTitle(month) })}</span>
        </div>
      </section>
      <p className="usage-note">{t('usage.postpaid.note', { min: n(usage.scan_min_chars_per_page) })}</p>

      <section className="usage-history">
        <div className="usage-month-nav">
          <button onClick={() => setMonth(shiftMonth(month, -1))} aria-label={t('usage.postpaid.previous')}>
            <ChevronLeft size={16} />
          </button>
          <h2 className="usage-packs-title">{monthTitle(month)}</h2>
          <button
            onClick={() => setMonth(shiftMonth(month, 1))}
            disabled={month === thisMonth}
            aria-label={t('usage.postpaid.next')}
          >
            <ChevronRight size={16} />
          </button>
        </div>
        {error ? (
          <div className="usage-error">{error}</div>
        ) : !data ? (
          <div className="usage-loading">{t('usage.loading')}</div>
        ) : data.rows.length === 0 ? (
          <div className="usage-loading">{t('usage.postpaid.empty')}</div>
        ) : (
          <div className="usage-table-scroll">
            <table className="usage-table usage-month-table">
              <thead>
                <tr>
                  <th>{t('usage.postpaid.date')}</th>
                  <th>{t('usage.postpaid.document')}</th>
                  <th className="num-col">{t('usage.postpaid.pages')}</th>
                  <th className="num-col">{t('usage.postpaid.read')}</th>
                  <th className="num-col">{t('usage.postpaid.billed')}</th>
                  {data.totals.free_chars > 0 && <th className="num-col">{t('usage.postpaid.free')}</th>}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => (
                  <tr key={`${row.created_at}-${row.document_id}`} className={row.charged ? '' : 'is-uncharged'}>
                    <td className="usage-tx-date">
                      {new Date(row.created_at).toLocaleString(i18n.language, {
                        day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                      })}
                    </td>
                    <td className="usage-tx-desc">
                      <span className="usage-month-doc">{row.filename || t('usage.postpaid.deleted')}</span>
                      <span className="usage-tx-note">
                        {' · '}
                        {t(`usage.postpaid.service.${row.service || 'translation'}`)}
                        {!row.charged && <> · {t('usage.postpaid.notCharged')}</>}
                      </span>
                    </td>
                    <td className="usage-tx-amt">{row.pages ?? '-'}</td>
                    <td className="usage-tx-amt">{row.chars_read != null ? n(row.chars_read) : '-'}</td>
                    <td className="usage-tx-amt">{n(row.billed_chars)}</td>
                    {data.totals.free_chars > 0 && (
                      <td className="usage-tx-amt">{row.free_chars ? n(row.free_chars) : '-'}</td>
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4}>{t('usage.postpaid.total', { count: data.totals.documents })}</td>
                  <td className="usage-tx-amt">{n(data.totals.billed_chars)}</td>
                  {data.totals.free_chars > 0 && <td className="usage-tx-amt">{n(data.totals.free_chars)}</td>}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
